import { useEffect, useState } from 'react';
import { emailHealthApi } from '../lib/api';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { AlertTriangle, Mail, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

/**
 * Top-of-dashboard banner that surfaces SMTP delivery failures so admins
 * notice broken email config (e.g. revoked SendGrid key) within minutes
 * instead of finding out from users days later.
 *
 * Polls every 60s. Only renders when failures_last_hour > 0.
 */
export const EmailHealthBanner = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      const res = await emailHealthApi.status();
      setData(res.data);
    } catch {
      // Silent — non-admins or transient errors shouldn't crash the dashboard.
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  if (!data || !data.failures_last_hour) return null;

  const handleClear = async () => {
    setClearing(true);
    try {
      await emailHealthApi.clear();
      toast.success('Email failure log cleared');
      await load();
    } catch {
      toast.error('Failed to clear');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Alert
      variant="destructive"
      className="border-red-300 bg-red-50 mb-6"
      data-testid="email-health-banner"
    >
      <AlertTriangle className="h-5 w-5 text-red-700" />
      <AlertTitle className="text-red-900 flex items-center gap-2">
        Email delivery is failing
        <span className="font-mono text-sm bg-red-100 px-2 py-0.5 rounded">
          {data.failures_last_hour} in last hour · {data.failures_last_24h} in last 24h
        </span>
      </AlertTitle>
      <AlertDescription className="text-red-900/90 mt-2">
        <p>
          The system tried to send emails (password resets, reminders, approvals) but the SMTP
          server rejected them. Real users are not receiving notifications right now.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Link to="/admin/config">
            <Button size="sm" variant="outline" data-testid="email-health-test-link">
              <Mail className="w-4 h-4 mr-1" />
              Test Email Config
            </Button>
          </Link>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            data-testid="email-health-expand"
          >
            {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            {expanded ? 'Hide details' : 'Show last failures'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClear}
            disabled={clearing}
            data-testid="email-health-clear"
          >
            <X className="w-4 h-4 mr-1" />
            Mark as resolved
          </Button>
        </div>

        {expanded && data.recent_failures?.length > 0 && (
          <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
            {data.recent_failures.map((f, i) => (
              <div
                key={f.id || i}
                className="text-xs bg-white/60 rounded p-2 font-mono"
                data-testid={`email-failure-${i}`}
              >
                <div className="font-semibold text-red-900">
                  {new Date(f.created_at).toLocaleString()} → {f.to}
                </div>
                <div className="text-slate-700 truncate">{f.subject}</div>
                <div className="text-red-700 mt-0.5 break-words">{f.error}</div>
              </div>
            ))}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
};
